import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { CHART_THEME } from "../../../constants/theme";
import { useGlobalCore } from "../../../cores/globals";
import { ChartMetric } from "../../../cores/analytics/interface/ChartMetric";
import {
  barLineChartBasicOptions,
  charBasicDataset,
  createGradientFill,
} from "../../utils/chartsUtils";

interface Props extends ChartMetric {
  forceLightTheme?: boolean;
}

export const SummaryLineChart = ({
  labels,
  datasets,
  forceLightTheme = false,
}: Props) => {
  const { isDarkMode } = useGlobalCore();
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setCapturing(Boolean(detail?.enabled));
    };
    window.addEventListener("summary-capture-change", handler);
    return () => window.removeEventListener("summary-capture-change", handler);
  }, []);

  const useLightTheme = forceLightTheme || capturing || !isDarkMode;

  const options = useMemo(() => {
    const tickColor = useLightTheme
      ? "rgba(74, 74, 94, 0.9)"
      : "rgba(255, 255, 255, 0.72)";
    const gridColor = useLightTheme
      ? "rgba(232, 232, 239, 0.9)"
      : "rgba(255, 255, 255, 0.1)";

    return {
      ...barLineChartBasicOptions,
      scales: {
        yAxes: [
          {
            ...barLineChartBasicOptions.scales.yAxes[0],
            ticks: {
              ...barLineChartBasicOptions.scales.yAxes[0].ticks,
              fontColor: tickColor,
            },
            gridLines: {
              ...barLineChartBasicOptions.scales.yAxes[0].gridLines,
              color: gridColor,
              zeroLineColor: gridColor,
            },
          },
        ],
        xAxes: [
          {
            ...barLineChartBasicOptions.scales.xAxes[0],
            ticks: {
              ...barLineChartBasicOptions.scales.xAxes[0].ticks,
              fontColor: tickColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
            },
            gridLines: {
              ...barLineChartBasicOptions.scales.xAxes[0].gridLines,
              color: gridColor,
              zeroLineColor: gridColor,
            },
          },
        ],
      },
    };
  }, [useLightTheme]);

  const data = useMemo(
    () => (canvas?: HTMLCanvasElement) => ({
      labels,
      datasets: datasets.map(({ label, color, data: values, fill, type }, index) => {
        const lineColor = color || CHART_THEME[index % CHART_THEME.length];
        return {
          ...charBasicDataset,
          label,
          borderColor: lineColor,
          pointBackgroundColor: lineColor,
          pointBorderColor: useLightTheme ? "#fff" : "#161b22",
          fill,
          type,
          backgroundColor: canvas
            ? createGradientFill(canvas, lineColor)
            : lineColor,
          data: [...values],
        };
      }),
    }),
    [datasets, labels, useLightTheme]
  );

  return (
    <div className="summary-analytics__chart-canvas">
      <Line data={data} options={options} redraw />
    </div>
  );
};
